import {
	DEFAULT_LAYER_MODE,
	type TLayerMode,
} from "@/store/modules/spreading-compaction-types";
import { computed, ref, watch } from "vue";
import {
	DEFAULT_ANGLE_TOLERANCE,
	MAX_ANGLE_TOLERANCE,
} from "./spreadingCompactionHelpers";
import type { SpreadingCompactionUnitContext } from "./useSpreadingCompactionUnit";

/**
 * UI-local composable for spreading/compaction feature.
 * Manages transient UI state only (input strings, open/close, debounce).
 */
export function useSpreadingCompactionForm(
	unitContext: Readonly<
		Pick<
			SpreadingCompactionUnitContext,
			| "convertDisplayToMm"
			| "convertMmToDisplay"
			| "quantizeDisplayValue"
			| "displayStep"
			| "unitConfig"
			| "formatDisplayInput"
		>
	>,
) {
	const spacingInput = ref(
		String(unitContext.unitConfig.value.defaultSpacingDisplay),
	);
	const slopeShoulderOffsetInput = ref(
		String(unitContext.unitConfig.value.defaultSlopeShoulderOffsetDisplay),
	);
	const toleranceInput = ref(String(DEFAULT_ANGLE_TOLERANCE));
	/** レイヤモードのラジオ選択値。エンティティ切替時に panel 側で再同期する */
	const layerModeInput = ref<TLayerMode>(DEFAULT_LAYER_MODE);
	const isResultExpanded = ref(true);
	const isFaceSelectionExpanded = ref(false);

	// The site unit can resolve after mount, so re-seed the defaults when it does.
	// Without this a ft site keeps the metric defaults and reads them as ft.
	watch(
		() => unitContext.unitConfig.value.distanceUnit,
		() => {
			spacingInput.value = String(
				unitContext.unitConfig.value.defaultSpacingDisplay,
			);
			slopeShoulderOffsetInput.value = String(
				unitContext.unitConfig.value.defaultSlopeShoulderOffsetDisplay,
			);
		},
	);

	const spacingValue = computed(() => {
		const parsed = Number.parseFloat(spacingInput.value);
		if (Number.isNaN(parsed)) return null;
		let displayValue = unitContext.quantizeDisplayValue(parsed);
		// A positive value finer than the granularity rounds to 0; clamp it up to
		// the smallest settable value instead of rejecting it.
		if (parsed > 0 && displayValue <= 0) {
			displayValue = unitContext.displayStep.value;
		}
		if (displayValue <= 0) return null;
		return unitContext.convertDisplayToMm(displayValue);
	});

	/**
	 * 法面肩オフセット (内部 mm、内側 = 負)。UI では「内側」を正の値で入力させるので符号を反転する。
	 * 負の入力 (外側) は受け付けない。
	 */
	const slopeShoulderOffsetValue = computed(() => {
		const parsed = Number.parseFloat(slopeShoulderOffsetInput.value);
		if (Number.isNaN(parsed)) return null;
		const displayValue = unitContext.quantizeDisplayValue(parsed);
		if (displayValue < 0) return null;
		return -unitContext.convertDisplayToMm(displayValue);
	});

	/** 内部 mm (内側 = 負) → UI 表示文字列 (内側 = 正) */
	function formatSlopeShoulderOffsetInput(internalMm: number): string {
		return unitContext.formatDisplayInput(-internalMm);
	}

	const toleranceValue = computed(() => {
		const v = Number.parseFloat(toleranceInput.value);
		return Number.isNaN(v) || v < 0 || v > MAX_ANGLE_TOLERANCE ? null : v;
	});

	return {
		spacingInput,
		slopeShoulderOffsetInput,
		toleranceInput,
		layerModeInput,
		isResultExpanded,
		isFaceSelectionExpanded,
		spacingValue,
		slopeShoulderOffsetValue,
		formatSlopeShoulderOffsetInput,
		toleranceValue,
	};
}
